// src/pages/OrderPage.js
import React, { useState, useEffect } from "react";
import "./OrderPage.css";
import { useCart } from "../context/CartContext";
import { useCheckout } from "../context/CheckoutContext"; // <-- global checkout context
import ItemModal from "../components/ItemModal";
import CheckoutModal from "../components/CheckoutModal";
import Basket from "../components/Basket";
import MenuPanel from "../components/MenuPanel";
import Toast from "../components/Toast";
import { Helmet } from "react-helmet";

const OrderPage = () => {
  const { cartItems } = useCart();
  const { openCheckout } = useCheckout(); // use global checkout functions

  const [selectedItem, setSelectedItem] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [openCategoryId, setOpenCategoryId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 901);
  const [toastMessage, setToastMessage] = useState("");

  // Responsive detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 901);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // CATEGORY CLICK + SCROLLING
  const handleCategoryClick = (id, initial = false) => {
    if (openCategoryId === id && !initial) {
      setOpenCategoryId(null);
      setActiveCategory(null);
    } else {
      setOpenCategoryId(id);
      setActiveCategory(id);

      // SCROLLING TO CATEGORY WITH OFFSET
      setTimeout(() => {
        const target = document.getElementById(`category-${id}`);
        if (target) {
          const offset = 60; 
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = target.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  const handleAddToCart = (item) => {
    setSelectedItem(item);
  };

  const isInCart = (itemId) =>
    cartItems.some(({ item }) => item.id === itemId);

  // Use global checkout instead of local state
  const handleCheckoutClick = (type) => {
    openCheckout(type || null);
  };

  return (
    <div className="order-page-container page-container">
      <Helmet>
        <title>Order Online | LasGidi Restaurant</title>
        <meta
          name="description"
          content="Order your favorite Nigerian dishes online from LasGidi Restaurant in Manchester. Enjoy quick delivery or takeaway."
        />
        <meta
          name="keywords"
          content="LasGidi Restaurant, Order Online, Nigerian Food Delivery, Manchester Takeaway, Food Delivery"
        />
        <meta property="og:title" content="Order Online | LasGidi Restaurant" />
        <meta
          property="og:description"
          content="Order your favorite Nigerian dishes online from LasGidi Restaurant in Manchester. Enjoy quick delivery or takeaway."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="left-container">
        <MenuPanel
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          handleCheckoutClick={handleCheckoutClick}
          handleCategoryClick={handleCategoryClick}
          activeCategory={activeCategory}
          openCategoryId={openCategoryId}
          isInCart={isInCart}
          cartItems={cartItems}
          handleAddToCart={handleAddToCart}
        />
      </div>

      {!isMobile && (
        <div className="right-container desktop-basket">
          <Basket onCheckoutClick={handleCheckoutClick} />
        </div>
      )}

      {isMobile && (
        <Basket onCheckoutClick={handleCheckoutClick} isMobileCheckout />
      )}

      {selectedItem && (
        <ItemModal
          isOpen={!!selectedItem}
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          showToast={(msg) => setToastMessage(msg)}
        />
      )}

      {/* Global CheckoutModal */}
      <CheckoutModal cartItems={cartItems} />

      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => setToastMessage("")}
        />
      )}
    </div>
  );
};

export default OrderPage;
